// React
import React, { useCallback, useMemo, useState } from "react";

// Types
import { WarehouseDropdownProps } from "./types";
import { DropdownOption } from "../Dropdown/types";
import { FleetWarehouseRow } from "services/api/fleet/warehouses/types";

// API
import { getFleetWarehousesAPI } from "services/api/fleet/warehouses/api";

// Pagination & Data
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// Hooks
import { DropdownCacheKey, buildDropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

// UI Components
import { DropdownAutocomplete } from "../DropdownAutocomplete";

// Pages / Cards
import WarehouseEditCard from "ui/modules/fleet/warehouses/pages/WarehouseDetailPage/components/WarehouseDetailCard/components/WarehouseEditCard/WarehouseEditCard";
import { Button } from "ui/components/Button/Button";



const WarehouseDropdown: React.FC<WarehouseDropdownProps> = (
  props: WarehouseDropdownProps
) => {
  const {
    name,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
    excludeIds,
    showAddNew = true,
  } = props;

  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const pagination = initialDataWithPagination.pagination;
  const cacheKey = buildDropdownCacheKey(DropdownCacheKey.FleetWarehouses, searchValue);

  const { data, isLoading, refresh } = useDropdownCache<DataWithPagination<FleetWarehouseRow>>(
    cacheKey,
    () => getFleetWarehousesAPI(pagination, searchValue),
    5 * 60 * 1000
  );

  const searchResults = data ?? initialDataWithPagination;

  const handleSearchClick = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((val: string) => {
    setSearchValue(val);
  }, []);

  const handleCreateWarehouse = useCallback(async (e: any, name: string) => {
    e.preventDefault();
    setShowAddNewModal(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    refresh();
    setShowAddNewModal(false);
  }, [refresh]);

  const warehouseItems: DropdownOption[] = useMemo(() => searchResults.data
    .filter((i) => !excludeIds?.includes(i.id))
    .map((i) => ({
      value: i.id,
      text: `${i.name}`,
    })), [searchResults.data, excludeIds]);

  return (
    <>
      <div className="flex items-end ">
        <div className="flex-1">
          <DropdownAutocomplete
            label={label}
            name={name}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            value={value}
            direction={direction}
            width={width}
            height={height}
            options={warehouseItems}
            onChange={onChange}
            onSearchChange={handleSearchChange}
            onSearchFocus={handleSearchClick}
            error={error}
            onCreate={showAddNew ? handleCreateWarehouse : undefined}
            isLoading={isLoading}
            inputClassName={!disabled && showAddNew ? "!rounded-r-none" : undefined}
          />
        </div>
        {!disabled && showAddNew && (
          <Button
            variant="outlined"
            startIcon="/media/icons/plus.svg"
            onClick={(e) => handleCreateWarehouse(e, "")}
            disabled={disabled}

          />
        )}
      </div>
      <WarehouseEditCard
        warehouseId={null}
        show={showAddNewModal}
        onClose={() => setShowAddNewModal(false)}
        onSuccess={handleEditSuccess}
      />
    </>
  );
};

export default React.memo(WarehouseDropdown);
